import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Check, Send } from 'lucide-react';
import {
  type PlatformNotification,
  listMyNotifications,
  markNotificationRead,
  sendNotification,
} from '@/api/platformNotifications';
import { useDirectory } from '@/hooks/useDirectory';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';

const TYPES = ['system', 'announcement', 'reminder', 'alert'];

function fmt(iso: string, locale: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(locale);
}

export function NotificationsAdminPage() {
  const { t, i18n } = useTranslation();
  const dir = useDirectory();

  const [inbox, setInbox] = useState<PlatformNotification[]>([]);
  const [recipient, setRecipient] = useState('');
  const [type, setType] = useState('announcement');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const loadInbox = useCallback(() => {
    listMyNotifications()
      .then(setInbox)
      .catch(() => setInbox([]));
  }, []);

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  async function send() {
    if (!recipient || !title.trim()) return;
    setSending(true);
    setSent(false);
    try {
      await sendNotification({ userId: recipient, type, title: title.trim(), body: body.trim() || undefined });
      setTitle('');
      setBody('');
      setSent(true);
      loadInbox();
    } finally {
      setSending(false);
    }
  }

  async function dismiss(id: string) {
    await markNotificationRead(id);
    setInbox((prev) => prev.filter((n) => n.id !== id));
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">
          {t('platformNotifications.title')}
        </h1>
        <p className="text-sm text-[var(--color-lop-text-secondary)]">
          {t('platformNotifications.subtitle')}
        </p>
      </header>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Send */}
        <div className="space-y-3 rounded-lg border bg-card p-4" data-testid="notif-send">
          <h2 className="flex items-center gap-2 text-sm font-medium text-[var(--color-lop-text-primary)]">
            <Send className="h-4 w-4" />
            {t('platformNotifications.send.heading')}
          </h2>
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('platformNotifications.send.recipient')}
            </label>
            <Select value={recipient} onChange={(e) => setRecipient(e.target.value)} data-testid="notif-recipient">
              <option value="">{t('platformNotifications.send.selectUser')}</option>
              {[...dir.users.values()].map((u) => (
                <option key={u.id} value={u.id}>
                  {dir.nameFor(u.id)}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('platformNotifications.send.type')}
            </label>
            <Select value={type} onChange={(e) => setType(e.target.value)}>
              {TYPES.map((ty) => (
                <option key={ty} value={ty}>
                  {t(`platformNotifications.type.${ty}`)}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('platformNotifications.send.titleField')}
            </label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="notif-title" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('platformNotifications.send.body')}
            </label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} />
          </div>
          <div className="flex items-center justify-between">
            {sent && <span className="text-xs text-emerald-400">{t('platformNotifications.send.sent')}</span>}
            <Button
              size="sm"
              className="ml-auto"
              onClick={send}
              disabled={sending || !recipient || !title.trim()}
              data-testid="notif-send-submit"
            >
              {t('platformNotifications.send.submit')}
            </Button>
          </div>
        </div>

        {/* My inbox */}
        <div className="rounded-lg border bg-card p-4">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-medium text-[var(--color-lop-text-primary)]">
            <Bell className="h-4 w-4" />
            {t('platformNotifications.inbox.heading')}
          </h2>
          {inbox.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t('platformNotifications.inbox.empty')}
            </p>
          ) : (
            <ul className="space-y-2">
              {inbox.map((n) => (
                <li
                  key={n.id}
                  className="flex items-start gap-3 rounded-md border border-[var(--color-lop-border)] p-3"
                  data-testid="notif-inbox-item"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[var(--color-lop-text-primary)]">{n.title}</p>
                    {n.body && (
                      <p className="text-xs text-[var(--color-lop-text-secondary)]">{n.body}</p>
                    )}
                    <p className="mt-1 text-[10px] text-[var(--color-lop-text-muted)]">
                      {t(`platformNotifications.type.${n.notifType}`, { defaultValue: n.notifType })} ·{' '}
                      {fmt(n.createdAt, i18n.language)}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => dismiss(n.id)} aria-label={t('platformNotifications.inbox.markRead')}>
                    <Check className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
